import React, { useState } from 'react';
import { AddCircleOutline, CheckCircle, LocationOnOutlined, QrCode2 } from '@mui/icons-material';
import { addressToString } from 'internal/business/maps/distances';
import { Avatar, Caption, Card, Grid, Link, PillButton, ProgressBar, Row, Stack, Text, TextButton } from 'library/ui';
import { Colors } from 'library';
import { getAvatarColor } from 'shared/avatarColors';
import { IBusinessLocation } from 'models';
import { IBusinessFaqQuestions } from 'parent-portal/home/Models/DashboardData';
import { useLocationRequirements } from './useLocationRequirements';
import { styled } from '@mui/material';

export default function BusinessLocationCard({
    businessLocation,
    faqQuestions,
}: {
    businessLocation: IBusinessLocation;
    faqQuestions: IBusinessFaqQuestions[];
}) {
    const [expanded, setExpanded] = useState(false);
    const { items, nextStep, unfilledCount, percentFilled } = useLocationRequirements({
        faqQuestions,
        businessLocation,
    });

    const detailsTo = `/profile/locations/${businessLocation.id}`;
    const contacts = businessLocation.business_contacts;
    const complete = percentFilled >= 100;

    return (
        <Card>
            <Stack gap="md">
                <Row justify="between" align="start" gap="md">
                    <Stack gap="none">
                        <Text weight="semibold">{businessLocation.name}</Text>
                        <Row gap="sm" align="center">
                            <LocationOnOutlined sx={{ fontSize: 16, color: Colors.textSecondary }} />
                            <Text tone="secondary">{addressToString(businessLocation.address)}</Text>
                        </Row>
                    </Stack>
                    <Row gap="sm">
                        <PillButton
                            variant="secondary"
                            to={`/clock-in/?loc_id=${businessLocation.address.id}`}
                            icon={<QrCode2 sx={{ fontSize: 16 }} />}
                        >
                            QR Code
                        </PillButton>
                        <PillButton variant="secondary" to={detailsTo}>
                            Edit
                        </PillButton>
                    </Row>
                </Row>

                {contacts.length > 0 && (
                    <Row gap="md" align="center">
                        <Row gap="none">
                            {contacts.slice(0, 3).map((c, i) => (
                                <OverlappingAvatar
                                    key={i}
                                    initials={`${c.user.first_name[0] ?? ''}${c.user.last_name[0] ?? ''}`.toUpperCase()}
                                    size={28}
                                    color={getAvatarColor(`${c.user.first_name} ${c.user.last_name}`)}
                                />
                            ))}
                        </Row>
                        <Text tone="secondary">
                            {contacts.map((c) => `${c.user.first_name} ${c.user.last_name[0]}`).join(', ')}
                        </Text>
                    </Row>
                )}

                <Stack gap="sm">
                    <Row justify="between" align="center">
                        <Text weight="semibold">Profile progress</Text>
                        <Text weight="semibold">{percentFilled}%</Text>
                    </Row>
                    <Row fullWidth>
                        <ProgressBar
                            value={percentFilled}
                            color={Colors.secondary}
                            backgroundColor={Colors.fill}
                            height={8}
                        />
                    </Row>
                </Stack>

                {complete ? (
                    <Card tone="success" padding="sm">
                        <Row gap="sm" align="center">
                            <CheckCircle sx={{ fontSize: 22, color: Colors.success }} />
                            <Text weight="medium">All set — nothing left to do here. Nice work!</Text>
                        </Row>
                    </Card>
                ) : (
                    <>
                        <Card tone="muted" padding="sm">
                            <Row justify="between" align="center" gap="md">
                                <Stack gap="none">
                                    <Text size="xs" weight="semibold" tone="accent" uppercase>
                                        Next step
                                    </Text>
                                    <Text weight="semibold">{nextStep}</Text>
                                </Stack>
                                <PillButton to={detailsTo}>Add now</PillButton>
                            </Row>
                        </Card>

                        <Row gap="sm" align="center">
                            <TextButton onClick={() => setExpanded((prev) => !prev)}>
                                {expanded ? 'Hide list' : 'See all'}
                            </TextButton>
                            <Caption>· {unfilledCount} items left to finish this profile</Caption>
                        </Row>

                        {expanded && (
                            <Grid columns={2} gap="sm" collapseAt="sm">
                                {items.map((item) => (
                                    <Card key={item.label} tone="muted" padding="xs">
                                        <Row justify="between" align="center" gap="sm">
                                            <Row gap="sm" align="center">
                                                {item.filled ? (
                                                    <CheckCircle sx={{ fontSize: 18, color: Colors.success }} />
                                                ) : (
                                                    <AddCircleOutline sx={{ fontSize: 18, color: Colors.border }} />
                                                )}
                                                <Text size="sm" tone={item.filled ? 'secondary' : 'primary'}>
                                                    {item.label}
                                                </Text>
                                            </Row>
                                            {item.filled ? <Caption>Added</Caption> : <Link to={detailsTo}>Add</Link>}
                                        </Row>
                                    </Card>
                                ))}
                            </Grid>
                        )}
                    </>
                )}
            </Stack>
        </Card>
    );
}

const OverlappingAvatar = styled(Avatar)({
    marginRight: -6,
});
